/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { NavigableBreadcrumbLink } from "../../../components/page_breadcrumbs/navigable_breadcrumb_link";
import {
  Box,
  Breadcrumbs,
  FormControl,
  IconButton,
  InputAdornment,
  Select,
  Typography
} from "@mui/material";
import {
  StyledBreadCrumbContainer,
  StyledLink,
} from "../../user_management/styles";
import {
  formControlStyles,
  selectFieldStyles,
} from "../employee_visibility/styles";
import { StyledFilterCancel } from "../../dashboard/styles";
import DropdownSearchBar from "../../../components/dorpdown_searchbar";
import { DropdownInfiniteLoader } from "../../../components/dropdown_infinite_loader";
import { ResetButton } from "../../../components/reset_button";
import { UpdateButton } from "../../../components/update_button";
import { SelectedMenuItem } from "../../../components/selected_menu_item";
import { keepPreviousData, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { selectBaseStyles, viewTypographyStyles } from "../styles";
import { StyledInputLabel } from "../../network_guests/styles";
import api from "../../../api";
import authenticatedFetch from "../../../api/authenticatedFetch";
import ClearIcon from '@mui/icons-material/Clear';
import { MenuHeading } from "../../../components/menu_heading";
import { Loader } from "../../../components/loader";
import { IndexTable } from "../../../components/index_table";
import camelize from "../../../utils/camelize";
import { formatDate } from "../../../utils/formatDate";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { datePickerStyles } from "../employee_insights/styles";
import dayjs from "dayjs";
import { buildQueryParams } from "../../../utils/buildQueryParams";
import { normalizeSearchTerm } from "../../../utils/normalizeSearchTerm";
import { useNotificationContext } from "../../../contexts/notificationContext";
import { OrderAppliedDiscountModal } from "../../../components/order_discount_modal";
import { FilterContainer } from "../../../components/filter_container";
import SearchIcon from '@mui/icons-material/Search';
import { StyledMenuItem } from "../../../components/styled_menu_item";
import { hasSingleItem } from "../../../utils/hasSingleItem";
import { getSingleItemValue } from "../../../utils/getSingleItemValue";
import { Query_Keys } from "../../../lib/queryKeys";
import { StyledConnectButton, StyledSearchBar } from "./styles";
import { PerPage } from "../../../constants/perPage";
import {
  getDropdownNextPageParam,
  mergeOptionsIfChanged,
  mergeUniqueByKey,
  normalizeRestaurant,
  normalizeRestaurantGroup,
} from "../../../utils/dropdownInfiniteQuery";
import { useDebounce } from "../../../utils/useDebounce";
import { useInfiniteScrollObserver } from "../../../hooks/useInfiniteScrollObserver";
import { AbilityContext } from "../../../contexts/abilityContext";
import { strictCanScope } from "../../../utils/caslStrict";
import { isValidDayjsValue } from "../../../utils/isValidDayjsValue";
import { isValidCustomDateRange } from "../../../utils/isValidCustomDateRange";
import { ConfirmationDialog } from "../../../components/confirmation_dialog";
import CheckIcon from '@mui/icons-material/Check';
import { startCase } from 'lodash'
import { useOtMutation } from "../../../hooks/mutations/useOtMutation";
import { reportMutationKeyGroups } from "../../../hooks/mutations/queryKeyGroups/reportMutationKeys";
import { OrderNumberCell } from "../../../components/order_number_cell";

const ALL_VALUE = 'All';
const EMPTY_DROPDOWN_OPTIONS = [];
const defaultFilter = {
  restaurant_group_id: ALL_VALUE,
  restaurant_sub_group_id: ALL_VALUE,
  restaurant_ids: [ALL_VALUE],
  dateFilter: ALL_VALUE,
  customDateFrom: null,
  customDateTo: null,
  order_status: ALL_VALUE,
}

export const NamedUserDiscounts = () => {
  const { ability } = useContext(AbilityContext)
  const [filtersChanged, setFiltersChanged] = useState(false)
  const [filters, setFilters] = useState(defaultFilter);
  const [appliedFilters, setAppliedFilters] = useState(defaultFilter)
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [page, setPage] = useState(1);
  const [restaurantSearch, setRestaurantSearch] = useState("");
  const [restaurantGroupSearch, setRestaurantGroupSearch] = useState('')
  const [restaurantSubGroupSearch, setRestaurantSubGroupSearch] = useState('')
  const [csvLoading, setCsvLoading] = useState(false)
  const showNotification = useNotificationContext();
  const [selectedOrder, setSelectedOrder] = useState('')
  const [orderOpen, setOrderOpen] = useState('')
  const [matchOrder, setMatchOrder] = useState('')
  const [matchOpen, setMatchOpen] = useState(false)
  const [matchOrderLoading, setMatchOrderLoading] = useState(false)
  const [sortBy, setSortBy] = useState('')
  const [sortOrder, setSortOrder] = useState('desc')
  const [search, setSearch] = useState('')

  const normalizedSearchTerm = useMemo(() => normalizeSearchTerm(search), [search]);
  const debouncedSearchTerm = useDebounce(normalizedSearchTerm, 300);
  const [restaurantOptionsCache, setRestaurantOptionsCache] = useState([])
  const [restaurantGroupOptionsCache, setRestaurantGroupOptionsCache] = useState([])
  const [restaurantSubGroupOptionsCache, setRestaurantSubGroupOptionsCache] = useState([])
  const debouncedRestaurantGroupSearch = useDebounce(restaurantGroupSearch.trim(), 300)
  const debouncedRestaurantSubGroupSearch = useDebounce(restaurantSubGroupSearch.trim(), 300)
  const debouncedRestaurantSearch = useDebounce(restaurantSearch.trim(), 300)

  const Days = [
    "Today",
    "Yesterday",
    "This Week",
    "Last 7 Days",
    "This Month",
    "Last Month",
    "Custom Date",
  ]

  const OrderStatus = [
    {
      label: 'Reconciled',
      value: 'reconciled',
    },
    {
      label: 'Not Reconciled',
      value: 'not_reconciled',
    },
  ]

  const formattedFilters = (sourceFilters = filters) => {
    const params = {};
    const today = dayjs();

    // Date range handling
    if (sourceFilters?.dateFilter !== "Custom Date" && sourceFilters?.dateFilter !== ALL_VALUE) {
      switch (sourceFilters.dateFilter) {
        case "Today":
          params.date_range = {
            from: today.format("DD/MM/YYYY"),
            to: today.format("DD/MM/YYYY"),
          };
          break;
        case "Last 7 Days":
          params.date_range = {
            from: today.subtract(7, "day").format("DD/MM/YYYY"),
            to: today.format("DD/MM/YYYY"),
          };
          break;
        case "Yesterday":
          params.date_range = {
            from: today.subtract(1, "day").format("DD/MM/YYYY"),
            to: today.subtract(1, "day").format("DD/MM/YYYY"),
          };
          break;
        case "This Week":
          params.date_range = {
            from: today.startOf("week").format("DD/MM/YYYY"),
            to: today.endOf("week").format("DD/MM/YYYY"),
          };
          break;
        case "This Month":
          params.date_range = {
            from: today.startOf("month").format("DD/MM/YYYY"),
            to: today.endOf("month").format("DD/MM/YYYY"),
          };
          break;
        case "Last Month":
          params.date_range = {
            from: today
              .subtract(1, "month")
              .startOf("month")
              .format("DD/MM/YYYY"),
            to: today.subtract(1, "month").endOf("month").format("DD/MM/YYYY"),
          };
          break;
      }
    } else if (sourceFilters?.dateFilter === "Custom Date" && sourceFilters?.customDateFrom && sourceFilters.customDateTo) {
      params.date_range = {
        from: dayjs(sourceFilters.customDateFrom).format("DD/MM/YYYY"),
        to: dayjs(sourceFilters.customDateTo).format("DD/MM/YYYY"),
      };
    }

    if (!sourceFilters?.restaurant_ids?.includes(ALL_VALUE)) {
      params.restaurant_ids = sourceFilters.restaurant_ids;
    }

    if (sourceFilters?.restaurant_group_id !== ALL_VALUE) {
      params.restaurant_group_id = sourceFilters.restaurant_group_id;
    }

    if (sourceFilters?.restaurant_sub_group_id && sourceFilters?.restaurant_sub_group_id !== ALL_VALUE) {
      params.restaurant_sub_group_id = sourceFilters.restaurant_sub_group_id;
    }

    if(sourceFilters?.order_status !== ALL_VALUE) {
      params.order_status = sourceFilters?.order_status
    }
    return params;
  }

  const fetchRestaurantGroupList = async ({ pageParam = 1 }) => {
    const response = await api.get("/v1/admin/dropdowns/restaurant_groups_list", {
      params: {
        page: pageParam,
        per_page: PerPage,
        search: debouncedRestaurantGroupSearch || undefined,
      },
    });
    return response?.data?.map(normalizeRestaurantGroup) || [];
  };

  const {
    data: restaurantGroups = [],
    isFetching: restaurantGroupFetching,
    isFetchingNextPage: restaurantGroupsFetchingNextPage,
    fetchNextPage: fetchNextRestaurantGroupsPage,
    hasNextPage: hasNextRestaurantGroupsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.restaurant_groups_dropdown({ search: debouncedRestaurantGroupSearch }),
    queryFn: fetchRestaurantGroupList,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => data.pages.flat(),
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const fetchRestaurantSubGroupList = async ({ pageParam = 1 }) => {
    const response = await api.get("/v1/admin/dropdowns/restaurant_sub_groups_list", {
      params: {
        page: pageParam,
        per_page: PerPage,
        search: debouncedRestaurantSubGroupSearch || undefined,
        restaurant_group_id:
          filters?.restaurant_group_id === ALL_VALUE ? undefined : filters?.restaurant_group_id,
      },
    });
    return response?.data || [];
  };

  const {
    data: restaurantSubGroups = EMPTY_DROPDOWN_OPTIONS,
    isFetching: restaurantSubGroupFetching,
    isFetchingNextPage: restaurantSubGroupsFetchingNextPage,
    fetchNextPage: fetchNextRestaurantSubGroupsPage,
    hasNextPage: hasNextRestaurantSubGroupsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.restaurant_sub_groups_dropdown({
      restaurant_group_id: filters?.restaurant_group_id,
      search: debouncedRestaurantSubGroupSearch,
    }),
    queryFn: fetchRestaurantSubGroupList,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => data.pages.flat(),
    enabled: filters?.restaurant_group_id !== ALL_VALUE,
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const fetchRestaurants = async ({ pageParam = 1 }) => {
    let res = await api.get("/v1/admin/dropdowns/restaurants_list", {
      params: {
        page: pageParam,
        per_page: PerPage,
        search: debouncedRestaurantSearch || undefined,
        restaurant_group_ids: filters?.restaurant_group_id === ALL_VALUE ? undefined : filters?.restaurant_group_id,
        restaurant_sub_group_id:
          filters?.restaurant_sub_group_id === ALL_VALUE ? undefined : filters?.restaurant_sub_group_id,
      }
    });
    return res?.data?.map(normalizeRestaurant) || [];
  };

  const {
    data: restaurants = [],
    isFetching: restaurantFetching,
    isFetchingNextPage: restaurantsFetchingNextPage,
    fetchNextPage: fetchNextRestaurantsPage,
    hasNextPage: hasNextRestaurantsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.restaurants_dropdown({
      restaurant_group_id: filters?.restaurant_group_id,
      restaurant_sub_group_id: filters?.restaurant_sub_group_id,
      search: debouncedRestaurantSearch,
    }),
    queryFn: fetchRestaurants,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => data.pages.flat(),
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const { rootRef: restaurantGroupMenuRef, targetRef: restaurantGroupLoaderRef } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextRestaurantGroupsPage,
    hasNextPage: hasNextRestaurantGroupsPage,
    isFetchingNextPage: restaurantGroupsFetchingNextPage,
  });

  const { rootRef: restaurantSubGroupMenuRef, targetRef: restaurantSubGroupLoaderRef } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextRestaurantSubGroupsPage,
    hasNextPage: hasNextRestaurantSubGroupsPage,
    isFetchingNextPage: restaurantSubGroupsFetchingNextPage,
  });

  const { rootRef: restaurantMenuRef, targetRef: restaurantLoaderRef } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextRestaurantsPage,
    hasNextPage: hasNextRestaurantsPage,
    isFetchingNextPage: restaurantsFetchingNextPage,
  });

  const fetchNamedUserDiscounts = async () => {
    const data = formattedFilters(appliedFilters)
    const response = await api.get('/v1/admin/reports/named_user_discounts', {
      params: {
        per_page: itemsPerPage,
        page: page,
        ...data,
        ...(debouncedSearchTerm ? { search: debouncedSearchTerm } : {}),
        ...(sortBy ? { sort_by: sortBy, sort_order: sortOrder } : {}),
      },
    })
    return { data: response?.data, pageCount: response?.headers["total-count"] }
  }

  const { data: namedUserDiscounts, isFetching } = useQuery({
    queryKey: Query_Keys.named_user_discounts({
      queryFilter: appliedFilters,
      page: page,
      itemsPerPage: itemsPerPage,
      search: debouncedSearchTerm,
      sortBy,
      sortOrder,
    }),
    queryFn: fetchNamedUserDiscounts,
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  })

  const fetchOrderAppliedDiscount = async () => {
    const response = await api.get(`/v1/admin/orders/${selectedOrder}/detail`)
    return response?.data
  }

  const { data: orderData, isLoading: orderLoading } = useQuery({
    queryKey: Query_Keys.applied_discount({
      selected_order: selectedOrder
    }),
    queryFn: fetchOrderAppliedDiscount,
    staleTime: 5 * 60 * 1000,
    enabled: selectedOrder !== ''
  })

  useEffect(() => {
    setRestaurantOptionsCache((currentOptions) => mergeUniqueByKey(currentOptions, restaurants, 'id'));
  }, [restaurants]);

  useEffect(() => {
    setRestaurantGroupOptionsCache((currentOptions) => mergeUniqueByKey(currentOptions, restaurantGroups, 'label'));
  }, [restaurantGroups]);

  useEffect(() => {
    setRestaurantSubGroupOptionsCache((currentOptions) => (
      mergeOptionsIfChanged(currentOptions, restaurantSubGroups, 'value', ['label'])
    ));
  }, [restaurantSubGroups]);

  const hasSingleAvailableRestaurantGroup = !debouncedRestaurantGroupSearch && hasSingleItem(restaurantGroups);
  const hasSingleAvailableRestaurant = !debouncedRestaurantSearch && hasSingleItem(restaurants);

  useEffect(() => {
    if (hasSingleAvailableRestaurantGroup) {
      const singleRestaurantGroup = getSingleItemValue(restaurantGroups);
      setFilters(prev => ({
        ...prev,
        restaurant_group_id: singleRestaurantGroup.label,
        restaurant_ids: [ALL_VALUE],
      }));
    }
  }, [hasSingleAvailableRestaurantGroup, restaurantGroups]);

  useEffect(() => {
    if (hasSingleAvailableRestaurant) {
      const singleRestaurant = getSingleItemValue(restaurants);
      setFilters(prev => ({
        ...prev,
        restaurant_ids: [singleRestaurant.id]
      }));
    }
  }, [hasSingleAvailableRestaurant, restaurants]);

  const getRestaurantGroupName = (id) => {
    if (id === ALL_VALUE) return 'All';
    const restaurantGroup = restaurantGroupOptionsCache?.find((res) => res.label === id)
    return restaurantGroup?.value ? restaurantGroup?.value : ''
  }

  const selectedRestaurantSubGroup = restaurantSubGroupOptionsCache?.find(
    (sub) => `${sub?.value}` === `${filters?.restaurant_sub_group_id}`
  )

  const displayedRestaurantSubGroups = selectedRestaurantSubGroup
    ? mergeUniqueByKey(restaurantSubGroups, [selectedRestaurantSubGroup], 'value')
    : restaurantSubGroups

  const getRestaurantSubGroupName = (id) => {
    if (id === ALL_VALUE) return 'All';
    const restaurantSubGroup = restaurantSubGroupOptionsCache?.find(
      (sub) => `${sub?.value}` === `${id}`
    )
    return restaurantSubGroup?.label || ''
  }

  const handleFilterChange = (field, value) => {
    setFilters((prev) => {
      const nextFilters = {
        ...prev,
        [field]: value,
      };

      if (field === "dateFilter" && value !== "Custom Date") {
        nextFilters.customDateFrom = null;
        nextFilters.customDateTo = null;
      }

      if (field === "customDateFrom") {
        if (!value) {
          nextFilters.customDateTo = null;
        } else if (isValidDayjsValue(value) && !isValidDayjsValue(prev.customDateTo)) {
          nextFilters.customDateTo = dayjs().startOf("day");
        }
      }

      return nextFilters;
    });
    setFiltersChanged(true);
  };

  const handleDropDownChange = (event) => {
    const {
      target: { value },
    } = event;

    setFiltersChanged(true);

    if ((value?.length === 1 && value.includes(ALL_VALUE)) || value?.length === 0) {
      setFilters({ ...filters, restaurant_ids: [ALL_VALUE] });
    }
    else {
      const newRestaurantIds = value.filter(val => val !== ALL_VALUE);
      setFilters({ ...filters, restaurant_ids: newRestaurantIds });
    }
  }

  const handleOrderStatusChange = (event) => {
    setFilters((prev => ({...prev, order_status: event.target.value})))
    setFiltersChanged(true);
  }

  const handleOrderStatusClear = () => {
    setFilters((prev => ({...prev, order_status: ALL_VALUE})))
    setFiltersChanged(true);
  }

  const handleRestaurantGroupChange = (event) => {
    setRestaurantSubGroupSearch('')
    setRestaurantSearch('')
    setFilters({
      ...filters,
      restaurant_group_id: event.target.value,
      restaurant_sub_group_id: ALL_VALUE,
      restaurant_ids: [ALL_VALUE],
    })
    setFiltersChanged(true)
  }

  const handleRestaurantSubGroupChange = (event) => {
    setRestaurantSearch('')
    setFilters({
      ...filters,
      restaurant_sub_group_id: event.target.value,
      restaurant_ids: [ALL_VALUE],
    })
    setFiltersChanged(true)
  }

  const handleRestaurantSubGroupClear = () => {
    setRestaurantSearch('')
    setFilters({
      ...filters,
      restaurant_sub_group_id: ALL_VALUE,
      restaurant_ids: [ALL_VALUE],
    })
    setFiltersChanged(true)
  }

  const handleDateRangeClear = () => {
    setFilters({ ...filters, dateFilter: ALL_VALUE, customDateFrom: null, customDateTo: null });
    setFiltersChanged(true)
  }

  const handleRestaurantGroupClear = () => {
    setRestaurantSubGroupSearch('')
    setRestaurantSearch('')
    setFilters({
      ...filters,
      restaurant_group_id: ALL_VALUE,
      restaurant_sub_group_id: ALL_VALUE,
      restaurant_ids: [ALL_VALUE],
    })
    setFiltersChanged(true)
  }

  const handleRestaurantClear = () => {
    setFilters({ ...filters, restaurant_ids: [ALL_VALUE] })
    setFiltersChanged(true);
  }

  const handleChange = () => {
    setFiltersChanged(false)
    setAppliedFilters(filters)
    setItemsPerPage(25);
    setPage(1);
  }

  const handleReset = () => {
    setFiltersChanged(false)
    setAppliedFilters(defaultFilter)
    setItemsPerPage(25);
    setPage(1);
    setFilters(defaultFilter)
    setSearch('')
  }

  const isCustomDateRangeValid =
    filters?.dateFilter !== "Custom Date" ||
    isValidCustomDateRange(filters?.customDateFrom, filters?.customDateTo);

  const isUpdateDisabled =
    filters?.dateFilter === "Custom Date" &&
    !isCustomDateRangeValid;

  const handleSearch = useCallback((searchQuery) => {
    setSearch(searchQuery)
    setPage(1)
  }, [])

  const getRestaurantDisplayValue = () => {
    if (!filters?.restaurant_ids || filters?.restaurant_ids?.length === 0) {
      return 'All';
    }

    if (filters?.restaurant_ids?.includes(ALL_VALUE)) {
      return 'All';
    }

    if (filters?.restaurant_ids?.length === 1 && restaurantOptionsCache) {
      const restaurant = restaurantOptionsCache.find(r => r.id === filters.restaurant_ids[0]);
      return restaurant ? restaurant.name : 'All';
    }

    return `${filters?.restaurant_ids?.length} Restaurants Selected`;
  }

  const handleOrderOpen = (order) => {
    setOrderOpen(true)
    setSelectedOrder(order)
  }

  const handleOrderClose = () => {
    setOrderOpen(false)
  }

  const handleExportCsv = async () => {
    try {
      setCsvLoading(true);
      const filterParams = formattedFilters()
      const query = buildQueryParams({
        ...filterParams,
        ...(debouncedSearchTerm ? { search: debouncedSearchTerm } : {}),
      })
      const url = `${process.env.REACT_APP_API_BASE_URL}/v1/admin/csv_reports/export?report=known_guests&${query}`;
      console.log(url)

      const { response } = await authenticatedFetch(url, {
        method: 'GET',
        defaultErrorMessage: 'Failed to download CSV'
      });

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = downloadUrl;
      let date = new Date
      let filename = `known_guests_${dayjs(date).format('YYYY-MM-DD')}.csv`

      a.download = filename;
      a.click();

      window.URL.revokeObjectURL(downloadUrl);

      showNotification('CSV downloaded successfully', 'success');
    } catch (error) {
      console.error('CSV download error:', error);
      showNotification(error?.message || 'Failed to download CSV', 'error');
    } finally {
      setCsvLoading(false);
    }
  }

  const getGuestName = (guest) => {
    if (guest?.first_name && guest?.last_name) {
      return `${guest?.first_name} ${guest?.last_name}`
    } else {
      return '-'
    }
  }

  const handleOrderMatch = (order) => {
    setMatchOrder(order)
    setMatchOpen(true)
  }

  const handleMatchClose = () => {
    setMatchOrder('')
    setMatchOpen(false)
  }

  const getErrorMessage = (errors = {}) => {
    const errorKey = Object.keys(errors)?.[0]

    switch (errorKey) {
      case 'discount_comment':
        return 'Discount comment cannot be blank'

      case 'order_id':
        return 'Order id cannot be blank'

      case 'restaurant_id':
        return 'Restaurant id cannot be blank'

      case 'order':
        return 'Order not found'

      case 'alias':
        return 'Alias not found'

      case 'mismatched_order':
        return 'Order does not belong to this restaurant'

      default:
        return 'Failed to match order'
    }
  }

  const { mutateAsync: connectNamedUserMutation } = useOtMutation({
    mutationFn: async (payload) => await api.post('/v1/admin/reports/connect_named_user', payload),
    invalidateKeys: [...reportMutationKeyGroups.connectNamedUser],
  });

  const handleMatchOrder = async () => {
    try {
      setMatchOrderLoading(true)
      await connectNamedUserMutation({
        order_id: matchOrder?.order_id,
        discount_comment: matchOrder?.discount_comment,
        restaurant_id: matchOrder?.restaurant?.id
      })
      showNotification('Order matched successfully', 'success')
    } catch (error) {
      const errorMessage = getErrorMessage(error?.response?.data?.errors)
      showNotification(errorMessage, 'error')
    } finally {
      setMatchOrderLoading(false)
      handleMatchClose()
    }
  }

  const tableColumns = [
    { key: 'order_number', label: 'Order #' },
    { key: 'order_details', label: 'Order Details', sortable: false },
    { key: 'order_date' },
    { key: 'restaurant' },
    { key: 'discount_name', label: 'Perq Name' },
    { key: 'discount_reason', label: 'Perq Reason' },
    { key: 'discount_comment', label: 'Perq Comment' },
    { key: 'discount_amount', label: 'Perq Amount' },
    { key: 'guest_name' },
    { key: 'job_title', sortable: false },
    { key: 'actions', sortable: false },
  ];

  const columns = tableColumns?.map((column) => {
    return {
      key: column.key,
      label: column.label || camelize(column.key),
      sortable: column.sortable,
    };
  });

  const TableRows = (data) => {
    return data?.map((record) => {
      const guest = getGuestName(record?.network_guest)

      return {
        order_number: record?.order_no,
        order_details: (
          <OrderNumberCell
            orderValue={record?.order_guid}
            onView={handleOrderOpen}
          />
        ),
        order_date: formatDate(record?.order_date, record?.time_zone_abbr),
        restaurant: record?.restaurant?.name,
        discount_name: record?.discount_name,
        discount_reason: record?.discount_reason,
        discount_comment: record?.discount_comment,
        discount_amount: `$${Number(record?.discount_amount).toFixed(2) ?? '0.00'}`,
        order_id: record?.order_id,
        guest_name: guest,
        job_title: record?.network_guest?.job_title || '-',
        actions: (
          <React.Fragment>
            <StyledConnectButton
              data-testid='connect-btn'
              variant="contained"
              disableRipple
              disabled={
                !(strictCanScope(ability, 'create', 'Restaurant', `${record?.restaurant?.id}`))
                || record?.network_guest !== null
              }
              onClick={() => handleOrderMatch(record)}
            >
              { record?.network_guest !== null ? 'Connected' : 'Connect' }
            </StyledConnectButton>
          </React.Fragment>
        )
      };
    });
  };

  const rows = TableRows(namedUserDiscounts?.data);

  const handleSortChange = (nextSortBy, nextSortOrder) => {
    setSortBy(nextSortBy)
    setSortOrder(nextSortOrder)
    setPage(1)
  }

  return (
    <React.Fragment>
      <Box sx={{
        height: "100%",
        width: "100%",
        overflowY: 'scroll',
        overflowX: 'hidden',
      }}
      >
        <StyledBreadCrumbContainer>
          <Breadcrumbs>
            <NavigableBreadcrumbLink LinkComponent={StyledLink} label="Home" to="/admin/dashboard" />
            <StyledLink color={"#515152"}>Reports</StyledLink>
            <StyledLink color={"#515152"}>Perq Reports</StyledLink>
            <StyledLink color={"#515152"}>Known Guests Report</StyledLink>
          </Breadcrumbs>
        </StyledBreadCrumbContainer>

        <FilterContainer>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", md: "center" },
              gap: { xs: 2, md: 0 }
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: { xs: 2, sm: "18px" },
                flexWrap: "wrap",
                width: { xs: "100%", md: "auto" },
                flex: 1
              }}
            >
              <React.Fragment>
                <Typography
                  variant="body2"
                  sx={{
                    ...viewTypographyStyles,
                    alignSelf: { xs: "flex-start", sm: "center" },
                    whiteSpace: "nowrap"
                  }}
                >
                  View
                </Typography>

                <FormControl
                  size="small"
                  sx={{
                    width: { xs: "100%", sm: "auto" },
                    minWidth: { xs: "100%", sm: "160px" }
                  }}
                >
                  <StyledInputLabel>Restaurant Group</StyledInputLabel>
                  <Select
                    size="small"
                    label="Restaurant Group"
                    MenuProps={{
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                      },
                      transformOrigin: {
                        vertical: "top",
                        horizontal: "left",
                      },
                      PaperProps: { ref: restaurantGroupMenuRef, sx: { maxHeight: "40vh" } },
                      autoFocus: false,
                      MenuListProps: { sx: { pt: 0 } },
                    }}
                    sx={{
                      ...selectBaseStyles,
                      minWidth: { xs: "100%", sm: "160px" },
                      width: { xs: "100%", sm: "160px" },
                      borderColor: "#2b4fb9",
                    }}
                    onChange={handleRestaurantGroupChange}
                    onClose={() => setTimeout(() => setRestaurantGroupSearch(''), 1000)}
                    value={hasSingleAvailableRestaurantGroup
                      ? getSingleItemValue(restaurantGroups)?.label
                      : (filters?.restaurant_group_id || ALL_VALUE)}
                    renderValue={() => {
                        if (hasSingleAvailableRestaurantGroup) {
                          return getSingleItemValue(restaurantGroups)?.value || 'All';
                        }
                        return getRestaurantGroupName(filters?.restaurant_group_id);
                      }}
                    endAdornment={
                      filters?.restaurant_group_id !== ALL_VALUE && !hasSingleAvailableRestaurantGroup ? (
                        <InputAdornment position="end" sx={{ position: 'absolute', right: 25 }}>
                          <IconButton
                            edge='end'
                            size="small"
                            disableRipple
                            data-testid="restaurant-group-clear-btn"
                            onClick={handleRestaurantGroupClear}
                          >
                            <ClearIcon sx={{ fontSize: '20px' }} />
                          </IconButton>
                        </InputAdornment>
                      ) : (
                        null
                      )
                    }
                  >
                    <DropdownSearchBar changeSearchText={setRestaurantGroupSearch} placeholder={'Search restaurant group'} isFetching={restaurantGroupFetching} />
                    {/* All option */}
                    {!hasSingleAvailableRestaurantGroup && (<StyledMenuItem
                      value={ALL_VALUE}
                    >
                      {filters?.restaurant_group_id === ALL_VALUE ? <SelectedMenuItem data="All" /> : "All"}
                    </StyledMenuItem>
                    )}
                    {
                      restaurantGroups
                        .map((res) => (
                          <StyledMenuItem
                            key={res?.label}
                            value={res?.label}
                          >
                            {
                              hasSingleAvailableRestaurantGroup || filters?.restaurant_group_id === res?.label ? <SelectedMenuItem data={res?.value} /> : res?.value
                            }
                          </StyledMenuItem>
                        ))
                    }
                    <DropdownInfiniteLoader
                      hasNextPage={hasNextRestaurantGroupsPage}
                      targetRef={restaurantGroupLoaderRef}
                    />
                  </Select>
                </FormControl>

                <FormControl
                  size="small"
                  disabled={filters?.restaurant_group_id === ALL_VALUE}
                  sx={{
                    width: { xs: "100%", sm: "auto" },
                    minWidth: { xs: "100%", sm: "180px" }
                  }}
                >
                  <StyledInputLabel>Restaurant Sub Group</StyledInputLabel>
                  <Select
                    size="small"
                    label="Restaurant Sub Group"
                    MenuProps={{
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                      },
                      transformOrigin: {
                        vertical: "top",
                        horizontal: "left",
                      },
                      PaperProps: { ref: restaurantSubGroupMenuRef, sx: { maxHeight: "40vh" } },
                      autoFocus: false,
                      MenuListProps: { sx: { pt: 0 } },
                    }}
                    sx={{
                      ...selectBaseStyles,
                      minWidth: { xs: "100%", sm: "180px" },
                      width: { xs: "100%", sm: "180px" },
                      borderColor: "#2b4fb9",
                    }}
                    onChange={handleRestaurantSubGroupChange}
                    onClose={() => setTimeout(() => setRestaurantSubGroupSearch(''), 1000)}
                    value={filters?.restaurant_sub_group_id || ALL_VALUE}
                    renderValue={() => getRestaurantSubGroupName(filters?.restaurant_sub_group_id)}
                    endAdornment={
                      filters?.restaurant_sub_group_id !== ALL_VALUE ? (
                        <InputAdornment position="end" sx={{ position: 'absolute', right: 25 }}>
                          <IconButton
                            edge='end'
                            size="small"
                            disableRipple
                            onClick={handleRestaurantSubGroupClear}
                          >
                            <ClearIcon sx={{ fontSize: '20px' }} />
                          </IconButton>
                        </InputAdornment>
                      ) : (
                        null
                      )
                    }
                  >
                    <DropdownSearchBar changeSearchText={setRestaurantSubGroupSearch} placeholder={'Search restaurant sub group'} isFetching={restaurantSubGroupFetching} />
                    {/* All option */}
                    <StyledMenuItem value={ALL_VALUE}>
                      {filters?.restaurant_sub_group_id === ALL_VALUE ? <SelectedMenuItem data="All" /> : "All"}
                    </StyledMenuItem>
                    {
                      displayedRestaurantSubGroups
                        .map((subGroup) => (
                          <StyledMenuItem
                            key={subGroup?.value}
                            value={subGroup?.value}
                          >
                            {
                              `${filters?.restaurant_sub_group_id}` === `${subGroup?.value}` ? <SelectedMenuItem data={subGroup?.label} /> : subGroup?.label
                            }
                          </StyledMenuItem>
                        ))
                    }
                    <DropdownInfiniteLoader
                      hasNextPage={hasNextRestaurantSubGroupsPage}
                      targetRef={restaurantSubGroupLoaderRef}
                    />
                  </Select>
                </FormControl>

                <FormControl
                  size="small"
                  sx={{
                    width: { xs: "100%", sm: "auto" },
                    minWidth: { xs: "100%", sm: "200px" }
                  }}
                >
                  <StyledInputLabel>Restaurant</StyledInputLabel>
                  <Select
                    size="small"
                    label="Restaurant"
                    MenuProps={{
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                      },
                      transformOrigin: {
                        vertical: "top",
                        horizontal: "left",
                      },
                      PaperProps: { ref: restaurantMenuRef, sx: { maxHeight: "40vh" } },
                      autoFocus: false,
                      MenuListProps: { sx: { pt: 0 } },
                    }}
                    sx={{
                      ...selectBaseStyles,
                      minWidth: { xs: "100%", sm: "200px" },
                      borderColor: "#2b4fb9",
                    }}
                    onChange={handleDropDownChange}
                    onClose={() => setTimeout(() => setRestaurantSearch(''), 1000)}
                    value={hasSingleAvailableRestaurant
                      ? [getSingleItemValue(restaurants)?.id]
                      : (filters?.restaurant_ids || [ALL_VALUE])
                    }
                    multiple
                    renderValue={() => {
                      if (hasSingleAvailableRestaurant) {
                        return getSingleItemValue(restaurants)?.name || 'All';
                      }
                      return getRestaurantDisplayValue();
                    }}
                    endAdornment={
                      !filters?.restaurant_ids?.includes(ALL_VALUE) && !hasSingleAvailableRestaurant ? (
                        <InputAdornment position="end" sx={{ position: 'absolute', right: 25 }}>
                          <IconButton
                            edge='end'
                            size="small"
                            disableRipple
                            data-testid="restaurant-clear-btn"
                            onClick={handleRestaurantClear}
                          >
                            <ClearIcon sx={{ fontSize: '20px' }} />
                          </IconButton>
                        </InputAdornment>
                      ) : (
                        null
                      )
                    }
                  >
                    <DropdownSearchBar changeSearchText={setRestaurantSearch} placeholder={'Search restaurant'} isFetching={restaurantFetching} />
                    {/* All option */}
                    {!hasSingleAvailableRestaurant && (<StyledMenuItem
                      key={ALL_VALUE}
                      value={ALL_VALUE}
                      disabled={!filters?.restaurant_ids?.includes(ALL_VALUE)}
                    >
                      {filters?.restaurant_ids?.includes(ALL_VALUE) ? <SelectedMenuItem data="All" /> : "All"}
                    </StyledMenuItem>
                    )}
                    {
                      restaurants
                        .map((res) => (
                          <StyledMenuItem
                            key={res?.id}
                            value={res?.id}
                          >
                            {
                              hasSingleAvailableRestaurant || filters?.restaurant_ids?.includes(res?.id) ? <SelectedMenuItem data={res?.name} /> : res?.name
                            }
                          </StyledMenuItem>
                        ))
                    }
                    <DropdownInfiniteLoader
                      hasNextPage={hasNextRestaurantsPage}
                      targetRef={restaurantLoaderRef}
                    />
                  </Select>
                </FormControl>

                <FormControl
                  size="small"
                  sx={{
                    width: { xs: "100%", sm: "auto" },
                    minWidth: { xs: "100%", sm: "160px" }
                }}
                >
                  <StyledInputLabel>Date Range</StyledInputLabel>
                  <Select
                    value={filters?.dateFilter || ALL_VALUE}
                    renderValue={() =>
                      filters?.dateFilter
                    }
                    label='Date Range'
                    size="small"
                    MenuProps={{
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                      },
                      transformOrigin: {
                        vertical: "top",
                        horizontal: "left",
                      },
                      PaperProps: { sx: { width: "230px" } },
                      autoFocus: false,
                    }}
                    sx={{
                      ...selectBaseStyles,
                      minWidth: { xs: "100%", sm: "160px" },
                      borderColor: "#2b4fb9",
                    }}
                    onChange={(e) => handleFilterChange("dateFilter", e.target.value)}
                    endAdornment={
                      filters?.dateFilter !== ALL_VALUE ? (
                        <InputAdornment position="end" sx={{ position: 'absolute', right: 25 }}>
                          <IconButton
                            edge='end'
                            size="small"
                            disableRipple
                            data-testid="date-range-clear-btn"
                            onClick={handleDateRangeClear}
                          >
                            <ClearIcon sx={{ fontSize: '20px' }} />
                          </IconButton>
                        </InputAdornment>
                      ) : (
                        null
                      )
                    }
                  >
                    {/* All option */}
                    <StyledMenuItem
                      value={ALL_VALUE}
                    >
                      {filters?.dateFilter === ALL_VALUE ? <SelectedMenuItem data="All" /> : "All"}
                    </StyledMenuItem>
                    {Days.map((value) => (
                      <StyledMenuItem
                        value={value}
                        key={value}
                      >
                        {
                          filters?.dateFilter === value ? (
                            <SelectedMenuItem data={value} />
                          ) : (
                            <>
                              {value}
                            </>
                          )
                        }
                      </StyledMenuItem>
                    ))}
                  </Select>
                </FormControl>
                {filters?.dateFilter === "Custom Date" && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      flexWrap: { xs: "wrap", sm: "nowrap" },
                      width: { xs: "100%", sm: "auto" }
                    }}
                  >
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <Typography variant="body2" sx={viewTypographyStyles}>
                        Days
                      </Typography>
                      <DatePicker
                        format="MM-DD-YYYY"
                        sx={{
                          ...datePickerStyles,
                          width: { xs: "100%", sm: "140px", lg: '160px' }
                        }}
                        slotProps={{ textField: { size: "small" } }}
                        value={filters?.customDateFrom || null}
                        onChange={(date) =>
                          handleFilterChange("customDateFrom", date)
                        }
                      />
                      <Typography variant="body2" sx={viewTypographyStyles}>
                        through
                      </Typography>
                      <DatePicker
                        sx={{
                          ...datePickerStyles,
                          width: { xs: "100%", sm: "140px", lg: '160px' }
                        }}
                        format="MM-DD-YYYY"
                        slotProps={{ textField: { size: "small" } }}
                        value={filters?.customDateTo || null}
                        onChange={(date) =>
                          handleFilterChange("customDateTo", date)
                        }
                      />
                    </LocalizationProvider>
                  </Box>
                )}
                <FormControl
                  size="small"
                  sx={formControlStyles}
                >
                  <StyledInputLabel>Order Status</StyledInputLabel>
                  <Select
                    data-testid='order-status'
                    size="small"
                    label="Order Status"
                    MenuProps={{
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                      },
                      transformOrigin: {
                        vertical: "top",
                        horizontal: "left",
                      },
                      PaperProps: { sx: { maxHeight: "40vh", width: '220px' } },
                      autoFocus: false,
                    }}
                    sx={selectFieldStyles}
                    value={filters?.order_status || ALL_VALUE}
                    onChange={handleOrderStatusChange}
                    renderValue={() => startCase(filters?.order_status)}
                    endAdornment={
                      filters?.order_status !== ALL_VALUE ? (
                        <StyledFilterCancel data-testid='order-status-clear'>
                          <IconButton
                            edge='end'
                            size="small"
                            disableRipple
                            onClick={handleOrderStatusClear}
                          >
                            <ClearIcon sx={{ fontSize: '20px' }} />
                          </IconButton>
                        </StyledFilterCancel>
                      ) : (
                        null
                      )
                    }
                  >
                  {/* All option */}
                  <StyledMenuItem
                    value={ALL_VALUE}
                  >
                    {filters?.order_status === ALL_VALUE ? <SelectedMenuItem data="All" /> : "All"}
                  </StyledMenuItem>
                  {
                    OrderStatus?.map((res) => (
                      <StyledMenuItem
                        key={res?.value}
                        value={res?.value}
                      >
                        {
                          filters?.order_status === res?.value ? <SelectedMenuItem data={res?.label} /> : res?.label
                        }
                      </StyledMenuItem>
                    ))
                  }
                </Select>
                </FormControl>
              </React.Fragment>
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                alignSelf: { xs: "flex-start", md: "center" },
                mt: { xs: 1, md: 0 },
                width: { xs: "100%", md: "auto" },
                justifyContent: { xs: "flex-start", md: "flex-end" }
              }}
            >
              <ResetButton
                onClick={handleReset}
                filtersChanged={filtersChanged}
                status={isFetching}
              />
              <UpdateButton
                onClick={handleChange}
                filtersChanged={filtersChanged}
                status={isFetching}
                disabled={isUpdateDisabled}
              />
            </Box>
          </Box>
        </FilterContainer>

        <MenuHeading
          report={false}
          dividerStyle={{ marginBottom: '16px' }}
          rightComponent={
            <StyledSearchBar
              value={search}
              placeholder="Search known guests"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              onChange={(e) => handleSearch(e.target.value)}
              autoComplete="off"
              inputProps={{ 'data-testid': 'known-guests-search-input' }}
            />
          }
        >
          Known Guests Report
        </MenuHeading>

        {
          (isFetching && !debouncedSearchTerm) ? (
            <Loader />
          ) : (
            <IndexTable
              columns={columns}
              data={rows}
              tableId="named-user-discounts"
              type="entries"
              sortMode="server"
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSortChange={handleSortChange}
              itemsPerPage={itemsPerPage}
              setItemsPerPage={setItemsPerPage}
              page={page}
              setPage={setPage}
              pageCount={namedUserDiscounts?.pageCount}
              report={false}
              showDownloadButton={true}
              disableDownloadButton={namedUserDiscounts?.data?.length === 0 || csvLoading}
              handleExportCsv={handleExportCsv}
              csvLoading={csvLoading}
              handleOpen={handleOrderOpen}
              searchLoading={isFetching && !!debouncedSearchTerm}
              hasSearchTerm={!!debouncedSearchTerm}
              emptyStateText="No Data Exists"
            />
          )
        }
      </Box>

      {/* Order Detail Modal */}
      <OrderAppliedDiscountModal
        open={orderOpen}
        handleClose={handleOrderClose}
        ordersData={orderData}
        isLoading={orderLoading}
      />

      {/* Match order confirmation dialog */}
      <ConfirmationDialog
        dataTestId={'match-order-dialog'}
        open={matchOpen}
        onClose={handleMatchClose}
        Icon={CheckIcon}
        dialogTitle={`Are you sure you want to connect this order ?`}
        dialogBody={'This action will try to match this order with network guest'}
        confirmationText={'Yes, I want to match this order'}
        handleConfirm={handleMatchOrder}
        isLoading={matchOrderLoading}
      />

    </React.Fragment>
  )
}
